import type { GetServerSideProps } from 'next';

import { getLocaleFromParams } from '@/utils/locale';
import { extractPathFromUrl } from '@/utils/urlUtils';

import { getIndexName } from '@/integrations/algolia/helpers';
import { algoliaSsrSearchClient } from '@/integrations/algolia/ssrClient';
import type { StoreHit } from '@/integrations/algolia/types';
import {
  BoutiqueDetailCMSTypeEnum,
  fetchCMSBoutiqueDetailContentWithExtraData,
  getStoreIdWithSlug,
} from '@/lib/boutique/helpers';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { BASE_STORE_FINDER_URL } from '@/lib/const';
import { capture404Event } from '@/lib/errors';
import { BOUTIQUE_DETAIL_PAGE_ERRORS } from '@/lib/notFoundErrors';
import {
  getContentfulRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType } from '@/lib/revalidate';
import { BoutiqueDetailViewWrapper } from '@/views/BoutiqueDetailView/index';

const LANG_MAP = {
  'en-US': 'en',
  'en-GB': 'en',
};

export const getServerSideProps: GetServerSideProps = commonServerSideProps(
  async (context) => {
    const routerLocale = getLocaleFromParams(context?.params);
    const { country, locale } = getRegionFromLocale(routerLocale);
    const storeSlug = context.params?.boutiuqueslug;
    const { countryCode } = getContentfulRegionFromLocale(routerLocale);
    try {
      const storeIdResult = await getStoreIdWithSlug(
        storeSlug as string,
        routerLocale,
        locale.slug,
      );
      if (storeIdResult === 'no hit' || !storeIdResult?.storeId) {
        capture404Event('Boutique detail page', {
          requestUrl: context.req.url
            ? extractPathFromUrl(context.req.url)
            : '',
          reason:
            BOUTIQUE_DETAIL_PAGE_ERRORS.BOUTIQUE_DETAIL_PAGE_STORE_ID_NOT_FOUND,
        });
        return {
          notFound: true,
        };
      }

      const { cmsExtraData, items, seoConfigBuilder } =
        await fetchCMSBoutiqueDetailContentWithExtraData(
          storeIdResult?.storeId,
          countryCode,
          locale.code,
          country.channelSlug,
          locale.locale,
          BoutiqueDetailCMSTypeEnum.Detail,
        );
      const newSeoVariables = {
        boutiqueName: storeIdResult.name,
      };
      seoConfigBuilder.mergeSeoVariables(newSeoVariables);
      if (!items || items.length === 0) {
        return {
          notFound: true,
        };
      } else {
        const lang: string =
          LANG_MAP[locale.locale as keyof typeof LANG_MAP] ??
          locale.locale ??
          'en';
        const indexName = getIndexName('STORE', routerLocale);
        const index = algoliaSsrSearchClient.initIndex(indexName);

        const searchResponse = await index.search('', {
          attributesToRetrieve: ['type'],
          facetFilters: [
            `store_id:${storeIdResult?.storeId}`,
            `web_visible:true`,
          ],
        });

        if (
          !['FLAG_SHIP_BOUTIQUE', 'BOUTIQUE']?.includes(
            (searchResponse.hits[0] as StoreHit)?.type,
          )
        ) {
          return {
            redirect: {
              destination: `${BASE_STORE_FINDER_URL}${lang.toLowerCase()}?storeId=${
                storeIdResult?.storeId
              }`,
              permanent: false,
            },
          };
        }

        return {
          props: {
            cmsBoutiqueDetailItems: items,
            cmsExtraData,
            resultId: storeIdResult?.storeId,
            seoConfig: seoConfigBuilder?.build(),
          },
        };
      }
    } catch (error) {
      capture404Event('Boutique detail page', {
        requestUrl: context.req.url ? extractPathFromUrl(context.req.url) : '',
        reason:
          BOUTIQUE_DETAIL_PAGE_ERRORS.BOUTIQUE_DETAIL_PAGE_RETRIEVE_STORE_ID_ERROR,
      });
      return {
        notFound: true,
      };
    }
  },
  CacheHeaderType.NO_CACHE,
);
export default BoutiqueDetailViewWrapper;
